import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { useTenantStore } from "../../stores/tenant";
import { joinTenant, useSocketEvent } from "../../hooks/use-socket";
import { api } from "../../lib/api";
import { toMs } from "../../lib/format";
import type { Call } from "@shared/types";

// Statuts considérés « en cours » : présents dans l'historique mais qui doivent
// aussi apparaître ici dès l'ouverture de l'onglet (pas seulement via socket).
const LIVE_STATUSES: Call["status"][] = ["in-progress", "ringing"];

export function CallsLive() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const [calls, setCalls] = useState<Call[]>([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    joinTenant(tenantId);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [tenantId]);

  // Amorçage : récupère les appels DÉJÀ en cours (sinon l'onglet reste vide tant
  // qu'aucun nouvel appel n'arrive, alors que l'historique les montre).
  useEffect(() => {
    if (!tenantId) return;
    api<Call[]>(`/api/tenants/${tenantId}/calls`)
      .then((list) => setCalls(list.filter((c) => LIVE_STATUSES.includes(c.status))))
      .catch(() => {
        /* hors-ligne : on s'appuiera sur les events socket */
      });
  }, [tenantId]);

  useSocketEvent<Call>("call:started", (call) => {
    setCalls((prev) => (prev.some((c) => c.id === call.id) ? prev : [call, ...prev]));
  });

  useSocketEvent<{ id: string }>("call:ended", ({ id }) => {
    setCalls((prev) => prev.filter((c) => c.id !== id));
  });

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Appels en direct</h1>
          <p className="page-header__subtitle">Les conversations en cours, en temps réel.</p>
        </div>
        <Badge variant="success">{calls.length} en cours</Badge>
      </header>
      <div className="calls-live">
        {calls.length === 0 ? (
          <p className="calls-live__empty">Aucun appel en cours.</p>
        ) : (
          <ul className="calls-live__list">
            {calls.map((c) => (
              <li key={c.id}>
                <Link to={`/calls/${c.id}`} className="calls-live__item" title="Voir le dialogue en direct">
                  <Badge variant="live">EN COURS</Badge>
                  <span className="calls-live__number">{c.fromNumber}</span>
                  <span className="calls-live__duration">
                    depuis {formatDuration(toMs(c.startedAt), now)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function formatDuration(startMs: number, now: number): string {
  const sec = Math.max(0, Math.floor((now - startMs) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
